import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  Injector,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxDropdownOptionsSource } from '@onyx/angular';
import {
  DropdownHelper,
  OnyxAutoFocusDirective,
  OnyxButtonComponent,
  OnyxDropdownComponent,
  OnyxIconComponent,
  OnyxMessageComponent,
  OnyxModalComponent,
  OnyxNamePipe,
  OnyxToastService,
} from '@onyx/angular';
import { CommonHelper } from '../../../../../common/helpers/common.helper';
import { chain } from 'lodash';
import { BehaviorSubject, map, of, Subject } from 'rxjs';
import {
  ValidationConditionsHelper,
  ValidationContext,
} from '../../../../../common/helpers/validation-conditions.helper';
import { ValidationHelper } from '../../../../../common/helpers/validation.helper';
import { FleetIdentifierPipe } from '../../../../../common/pipes/fleet-identifier.pipe';
import { VEHICLE_CATEGORIES } from '../../../../fleet/common/constants/vehicle-categories';
import { FleetState } from '../../../../fleet/common/enums/fleet-state';
import { FleetValidationHelper } from '../../../../fleet/common/helpers/fleet-validation.helper';
import { FleetHelper } from '../../../../fleet/common/helpers/fleet.helper';
import type { Fleet } from '../../../../fleet/common/interfaces/fleet';
import { FleetService } from '../../../../fleet/common/services/fleet.service';
import { TelematicsHelper } from '../../../../map/common/helpers/telematics.helper';
import type { Driver, SimplifiedDriver } from '../../interfaces/driver';

export type DriverAssignedVehicleModalData = Driver;

@Component({
  selector: 'app-driver-assigned-vehicle-modal',
  imports: [
    OnyxModalComponent,
    OnyxDropdownComponent,
    ReactiveFormsModule,
    OnyxButtonComponent,
    TranslatePipe,
    OnyxIconComponent,
    OnyxMessageComponent,
    OnyxIconComponent,
    FleetIdentifierPipe,
    OnyxNamePipe,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './driver-assigned-vehicle-modal.component.html',
  styleUrl: './driver-assigned-vehicle-modal.component.scss',
})
export class DriverAssignedVehicleModalComponent implements OnInit {
  protected readonly driver =
    inject<DriverAssignedVehicleModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly fleetService = inject(FleetService);
  private readonly toastService = inject(OnyxToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly fleetHelper = inject(FleetHelper);
  private readonly fleetValidationHelper = inject(FleetValidationHelper);
  private readonly fleetIdentifierPipe = inject(FleetIdentifierPipe);
  private readonly validationConditionsHelper = inject(
    ValidationConditionsHelper,
  );

  protected readonly I18N = 'drivers.assignVehicleModal';
  protected readonly VEHICLES_SOURCE: OnyxDropdownOptionsSource<Fleet> = {
    list: (query, pagination) =>
      this.fleetService
        .searchFleet(query, {
          ...pagination,
          filters: [
            FleetHelper.createCategoryFilter([...VEHICLE_CATEGORIES]),
            FleetHelper.createStateFilter(FleetState.ACTIVE),
          ],
        })
        .pipe(
          map((result) => {
            if (!this.driver) return result;

            return {
              ...result,
              options: chain(DropdownHelper.mapToOptions(result.options)!)
                .map(
                  (option) =>
                    this.fleetValidationHelper.validateDriver({
                      context: ValidationContext.DRIVER,
                      vehicle: option.value,
                      options: {
                        driver: this.driver,
                        option,
                      },
                    }).option!,
                )
                .thru((options) =>
                  this.validationConditionsHelper.groupOptions(options),
                )
                .value(),
            };
          }),
        ),
    get: (uuid) =>
      this.fleetService.getFleet(uuid).pipe(
        map((vehicle) => ({
          name: this.fleetIdentifierPipe.transform(vehicle),
          value: vehicle,
        })),
      ),
    idKey: 'uuid',
  };
  protected readonly TRAILERS_SOURCE = this.fleetHelper.getTrailersSource();

  protected readonly driverIcon = TelematicsHelper.getDriverIcon(this.driver);

  protected form = this.buildForm();
  protected loading = signal(false);
  protected vehicle$ = new BehaviorSubject<Fleet | null>(null);
  protected vehicle = signal<Fleet | null>(null);
  protected trailer = signal<Fleet | null>(null);
  protected close$ = new Subject<void>();
  protected existingDriver = signal<SimplifiedDriver | null>(null);

  protected trailerLabel = computed(() =>
    FleetHelper.getTrailerLabel(this.vehicle()),
  );

  protected links = computed(() => ({
    vehicle: CommonHelper.getPreviewLink(this.vehicle(), (vehicle) =>
      this.fleetHelper.openModal(vehicle),
    ),
    trailer: CommonHelper.getPreviewLink(this.trailer(), (trailer) =>
      this.fleetHelper.openModal(trailer),
    ),
  }));

  constructor() {
    this.vehicle$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((vehicle) => {
        this.vehicle.set(vehicle);
        this.form.controls.trailerUuid.setValue(vehicle?.trailer?.uuid ?? null);

        const existingDriver = Object.values(vehicle?.drivers ?? {}).find(
          (driver) => driver && driver.uuid !== this.driver.uuid,
        );
        this.existingDriver.set(existingDriver ?? null);
      });
  }

  public ngOnInit(): void {
    this.form.patchValue({
      vehicleUuid: this.driver.assignedVehicle?.uuid ?? null,
    });
  }

  protected openFleetModal(fleet: Fleet | null): void {
    if (fleet) this.fleetHelper.openModal(fleet);
  }

  protected async submit(): Promise<void> {
    if (!(await ValidationHelper.checkValidity(this.form, this.injector))) {
      return;
    }

    const vehicleUuid = this.form.controls.vehicleUuid.getRawValue();

    let action$;
    if (vehicleUuid) {
      action$ = this.fleetService.updateAssignedDrivers(vehicleUuid, {
        primaryDriverUuid: this.existingDriver()?.uuid ?? this.driver.uuid,
        secondaryDriverUuid: this.existingDriver() ? this.driver.uuid : null,
      });
    } else if (this.driver.assignedVehicle) {
      action$ = this.fleetService.unassignDriver(
        this.driver.assignedVehicle.uuid,
        this.driver.uuid,
      );
    } else {
      action$ = of(undefined);
    }

    this.loading.set(true);
    action$
      .subscribe(() => {
        this.toastService.showSuccess({ message: `${this.I18N}.success` });
        this.close$.next();
      })
      .add(() => this.loading.set(false));
  }

  private buildForm() {
    return this.fb.group({
      vehicleUuid: this.fb.control<string | null>(null),
      trailerUuid: this.fb.control<string | null>({
        value: null,
        disabled: true,
      }),
    });
  }
}
